/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Typography,
    Paper,
    Switch,
    FormControlLabel,
    Button,
    Box,
    Alert,
    CircularProgress,
    Grid,
    Divider,
    Card,
    CardContent,
    TextField,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tooltip
} from '@mui/material';
import {
    Notifications as NotificationsIcon,
    Email as EmailIcon,
    PhoneIphone as PushIcon,
    Apps as InAppIcon,
    Schedule as ScheduleIcon,
    ExpandMore as ExpandMoreIcon,
    Sms as SmsIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import api from '../../utils/axiosConfig';
import { usePWAStatus } from '../../hooks/usePWAStatus';
import { notifications, runtime } from '../../platform';

const NotificationPreferences = () => {
    const navigate = useNavigate();
    const user = useSelector((state) => state.auth.user);
    const activeUnit = useSelector((state) => state.auth.activeUnit);
    const { isStandalone } = usePWAStatus();
    const isAppShell = runtime.isNative() || isStandalone;
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [pushEnabled, setPushEnabled] = useState(false);
    const [pushLoading, setPushLoading] = useState(false);
    const [pushError, setPushError] = useState('');
    const [pushSuccess, setPushSuccess] = useState('');
    const [hasPushSubscription, setHasPushSubscription] = useState(false);
    const [pushPermission, setPushPermission] = useState('default');
    const [availableTemplates, setAvailableTemplates] = useState([]);
    const [expandedCategories, setExpandedCategories] = useState({
        caregiver: false,
        alert: false,
        system: false
    });
    
    // Check if user has emergency alert capable unit
    const smartConnectUnit = useSelector((state) => {
        const mc = state.auth.activeUnit?.MC || "";
        return /smart connect/i.test(mc);
    });
    
    // Structure: { category: { default: {...}, templates: { templateName: {...} } } }
    const [preferences, setPreferences] = useState({});

    const hasPushEnabledOnCurrentDevice = pushEnabled && hasPushSubscription;

    const categoryDescriptions = {
        caregiver: 'Notifications about caregiver activities and updates',
        alert: 'Critical alerts and emergency notifications',  
        system: 'System updates, maintenance, and general information'
    };

    const categoryIcons = {
        caregiver: <NotificationsIcon />,
        alert: <NotificationsIcon color="error" />,
        system: <NotificationsIcon color="info" />
    };

    const deliveryMethodTooltips = {
        email: 'Send notifications to your registered email address. You will receive detailed email notifications that you can review at your convenience.',
        push: 'Send instant push notifications to your mobile device. These appear as pop-up alerts even when the app is not open.',
        sms: 'Send text message alerts to your mobile phone. Receive instant SMS notifications for critical emergency alerts.'
    };

    useEffect(() => {
        fetchPreferences();
        void checkPushStatus();

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') void checkPushStatus();
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, []);

    const checkPushStatus = async () => {
        try {
            const status = await notifications.getStatus();
            setHasPushSubscription(status.subscription !== null);
            setPushEnabled(status.enabled);
            setPushPermission(status.permission);
        } catch (error) {
            console.error('Error checking push notification status:', error);
        }
    };

    const enablePushNotifications = async () => {
        setPushLoading(true);
        setPushError('');
        setPushSuccess('');

        try {
            const result = runtime.isNative() ? await notifications.enable({ sendTest: true }) : await notifications.enable();

            setPushEnabled(true);
            setHasPushSubscription(true);
            setPushPermission('granted');
            if (runtime.isNative() && result?.testAccepted === false) {
                setPushError('Notifications are enabled, but the test could not be sent.');
            } else setPushSuccess(runtime.isNative() ? 'Notifications enabled. A test notification was sent to this device.' : 'Push notifications enabled successfully.');
        } catch (error) {
            console.error('Error enabling push notifications:', error);
            setPushError(error.message || 'Failed to enable push notifications');
        } finally {
            setPushLoading(false);
        }
    };

    const disablePushNotifications = async () => {
        setPushLoading(true);
        setPushError('');
        setPushSuccess('');

        try {
            await notifications.disable();

            setPushEnabled(false);
            setHasPushSubscription(false);
            setPushSuccess('Push notifications disabled successfully.');
        } catch (error) {
            console.error('Error disabling push notifications:', error);
            setPushError(error.message || 'Failed to disable push notifications');
        } finally {
            setPushLoading(false);
        }
    };

    const handlePushToggle = async () => {
        if (pushEnabled) {
            await disablePushNotifications();
            return;
        }
        await enablePushNotifications();
    };

    const fetchPreferences = async () => {
        setLoading(true);
        setError('');
        
        try {
            const response = await api.get('/api/notifications/preferences');
            const { preferences: prefsArray, availableTemplates: templates } = response.data;
            // Filter templates based on user's unit type and one-time notifications
            const filteredTemplates = templates.filter(tmpl => {
                // Hide welcome_new_caregiver - it's a one-time notification
                if (tmpl.TemplateName === 'welcome_new_caregiver') {
                    return false;
                }
                // Hide smart_connect unless user has emergency alert capable unit
                if (tmpl.TemplateName.includes('smart_connect') && !smartConnectUnit) {
                    return false;
                }
                return true;
            });
            
            setAvailableTemplates(filteredTemplates);
            
            // Build preference structure organized by category
            const organized = {};
            
            // Group templates by category
            const templatesByCategory = filteredTemplates.reduce((acc, tmpl) => {
                if (!acc[tmpl.Category]) acc[tmpl.Category] = [];
                acc[tmpl.Category].push(tmpl);
                return acc;
            }, {});
            
            // Process each category
            ['caregiver', 'alert', 'system'].forEach(category => {
                organized[category] = {};
                
                // Find template-specific preferences for this category
                if (templatesByCategory[category]) {
                    templatesByCategory[category].forEach(tmpl => {
                        const templatePref = prefsArray.find(p => 
                            p.TemplateName === tmpl.TemplateName
                        );
                        
                        if (templatePref) {
                            organized[category][tmpl.TemplateName] = {
                                EmailEnabled: templatePref.EmailEnabled === 1,
                                PushEnabled: templatePref.PushEnabled === 1,
                                InAppEnabled: templatePref.InAppEnabled === 1,
                                SMSEnabled: templatePref.SMSEnabled === 1
                            };
                        } else {
                            // Use system defaults if no preference exists yet
                            organized[category][tmpl.TemplateName] = {
                                EmailEnabled: true,
                                PushEnabled: category !== 'system',
                                InAppEnabled: true,
                                SMSEnabled: false // Default SMS to false unless specified
                            };
                        }
                    });
                }
            });
            
            setPreferences(organized);
        } catch (error) {
            console.error('Error fetching preferences:', error);
            setError('Failed to load notification preferences');
        } finally {
            setLoading(false);
        }
    };

    const handlePreferenceChange = (category, field, value, templateName) => {
        setPreferences(prev => {
            const updated = { ...prev };
            
            if (!updated[category][templateName]) {
                updated[category][templateName] = {
                    EmailEnabled: true,
                    PushEnabled: true,
                    InAppEnabled: true
                };
            }
            updated[category][templateName][field] = value;
            
            return updated;
        });
        
        // Clear success message when user makes changes
        if (success) setSuccess('');
    };

    const handleAccordionChange = (category) => (event, isExpanded) => {
        setExpandedCategories(prev => ({
            ...prev,
            [category]: isExpanded
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        setError('');
        setSuccess('');

        try {
            // Convert preferences object to array format expected by backend
            const preferencesArray = [];
            
            Object.keys(preferences).forEach(category => {
                const categoryTemplates = preferences[category];
                
                // Add each template preference
                Object.keys(categoryTemplates).forEach(templateName => {
                    const templatePref = categoryTemplates[templateName];
                    // Find the category for this template from availableTemplates
                    const template = availableTemplates.find(t => t.TemplateName === templateName);
                    preferencesArray.push({
                        Category: template?.Category || category,
                        TemplateName: templateName,
                        EmailEnabled: templatePref.EmailEnabled,
                        PushEnabled: templatePref.PushEnabled,
                        InAppEnabled: templatePref.InAppEnabled,
                        SMSEnabled: templatePref.SMSEnabled || false,
                        QuietHoursStart: null,
                        QuietHoursEnd: null
                    });
                });
            });

            await api.put('/api/notifications/preferences', {
                preferences: preferencesArray
            });

            setSuccess('Notification preferences saved successfully!');
        } catch (error) {
            console.error('Error saving preferences:', error);
            if (error.response && error.response.data && error.response.data.message) {
                setError(error.response.data.message);
            } else {
                setError('Failed to save notification preferences. Please try again.');
            }
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {isAppShell && (runtime.isNative() || !hasPushEnabledOnCurrentDevice) && (
            <Paper sx={{ p: 2, mb: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <PushIcon color="primary" />
                        <Box>
                            <Typography variant="h6">Allow Push Notifications</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Allow alerts even when the app is closed.
                            </Typography>
                        </Box>
                    </Box>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={pushEnabled}
                                onChange={handlePushToggle}
                                disabled={!notifications.isSupported() || pushLoading}
                            />
                        }
                        label={pushEnabled
                            ? 'Allowed on this device'
                            : pushPermission === 'denied'
                                ? 'Disabled in device settings'
                                : 'Not enabled on this device'}
                        sx={{ mr: 0 }}
                    />
                </Box>

                {pushPermission === 'denied' && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        Push notifications are disabled for Connect App 365 in this device's system settings.
                        Enable notifications there, then return to the app and try again.
                    </Alert>
                )}

                {!notifications.isSupported() && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        Push notifications are not supported in this browser.
                    </Alert>
                )}

                {isAppShell && pushEnabled && hasPushSubscription && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                        Push notifications are enabled for this device.
                    </Alert>
                )}

                {pushError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {pushError}
                    </Alert>
                )}

                {pushSuccess && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                        {pushSuccess}
                    </Alert>
                )}
            </Paper>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    {success}
                </Alert>
            )}

            {Object.keys(preferences).map((category) => {
                const categoryTemplates = availableTemplates.filter(t => t.Category === category);
                const categoryPrefs = preferences[category];
                
                if (!categoryPrefs || categoryTemplates.length === 0) return null;
                
                return (
                    <Accordion 
                        key={category} 
                        sx={{ mb: 2 }}
                        expanded={expandedCategories[category]}
                        onChange={handleAccordionChange(category)}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box display="flex" alignItems="center" gap={2}>
                                {categoryIcons[category]}
                                <Box>
                                    <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                                        {category} Notifications
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {categoryDescriptions[category]}
                                    </Typography>
                                </Box>
                            </Box>
                        </AccordionSummary>
                        
                        <AccordionDetails>
                            <Box>
                                {categoryTemplates.map(template => {
                                    const templatePref = categoryPrefs[template.TemplateName] || {
                                        EmailEnabled: true,
                                        PushEnabled: category !== 'system',
                                        InAppEnabled: true
                                    };
                                    return (
                                        <Box 
                                            key={template.TemplateName}
                                            sx={{ 
                                                mb: 2, 
                                                p: 2, 
                                                backgroundColor: 'background.paper',
                                                borderRadius: 1,
                                                border: '1px solid',
                                                borderColor: 'divider'
                                            }}
                                        >
                                            <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                                                {template.Description || template.TemplateName}
                                            </Typography>
                                            <Box display="flex" gap={2} flexWrap="wrap">
                                                <Tooltip title={deliveryMethodTooltips.email} arrow placement="top">
                                                    <FormControlLabel
                                                        key={`${template.TemplateName}-email`}
                                                        control={
                                                            <Switch
                                                                checked={templatePref.EmailEnabled}
                                                                onChange={(e) => handlePreferenceChange(category, 'EmailEnabled', e.target.checked, template.TemplateName)}
                                                                color="primary"
                                                                size="small"
                                                            />
                                                        }
                                                        label={
                                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                                <EmailIcon fontSize="small" />
                                                                <Typography variant="body2">Email</Typography>
                                                            </Box>
                                                        }
                                                    />
                                                </Tooltip>
                                                <Tooltip title={deliveryMethodTooltips.push} arrow placement="top">
                                                    <FormControlLabel
                                                        key={`${template.TemplateName}-push`}
                                                        control={
                                                            <Switch
                                                                checked={templatePref.PushEnabled}
                                                                onChange={(e) => handlePreferenceChange(category, 'PushEnabled', e.target.checked, template.TemplateName)}
                                                                color="primary"
                                                                size="small"
                                                            />
                                                        }
                                                        label={
                                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                                <PushIcon fontSize="small" />
                                                                <Typography variant="body2">Push</Typography>
                                                            </Box>
                                                        }
                                                    />
                                                </Tooltip>
                                                {/* SMS option - only for smart_connect when user has SmartConnect unit */}
                                                {template.TemplateName.includes('smart_connect') && smartConnectUnit && template.Category === 'alert' &&  (
                                                    <Tooltip title={deliveryMethodTooltips.sms} arrow placement="top">
                                                        <FormControlLabel
                                                            key={`${template.TemplateName}-sms`}
                                                            control={
                                                                <Switch
                                                                    checked={templatePref.SMSEnabled || false}
                                                                    onChange={(e) => handlePreferenceChange(category, 'SMSEnabled', e.target.checked, template.TemplateName)}
                                                                    color="primary"
                                                                    size="small"
                                                                />
                                                            }
                                                            label={
                                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                                    <SmsIcon fontSize="small" />
                                                                    <Typography variant="body2">SMS</Typography>
                                                                </Box>
                                                            }
                                                        />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                );
            })}

            {/* Save Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={20} /> : null}
                >
                    {saving ? 'Saving...' : 'Save Preferences'}
                </Button>
            </Box>
        </Box>
    );
};

export default NotificationPreferences;
